/**
 * Copy for the Overview activity feed.
 *
 * The wire's `EngagementActivityEvent` carries no payload, no summary and no
 * money — just `eventType`, the actor, the state pair and an optional reason
 * (plan §2). So the sentence is composed client-side from the event type, with
 * the actor's name supplied by the caller.
 *
 * An event type this build doesn't know about renders its raw name rather than
 * being dropped: a feed that silently omits events reads as "nothing happened",
 * which is worse than an ugly line.
 *
 * The two channel events say "a message channel" and not WHICH one, and that is
 * the wire's ceiling rather than a copy shortcut. Three channels exist
 * (`engagement/service/messaging.go:107-109`: trilateral, of_client, of_expert)
 * and the api does stamp `channel_type` into the audit payload
 * (`messaging_membership.go:118`), but `EngagementActivityEvent` declares no
 * payload field at all and `TestEngagementActivityEventTypeHasNoPayloadOrMoneyField`
 * holds it that way. The consequence to know: a customer may read one of these
 * rows as being about their own channel when it is the OF-to-expert one. Naming
 * the channel is not a copy edit — it needs a new payload field on that type and
 * breaks that guard test, so it is a schema decision, not this file's.
 */
const EVENT_COPY: Record<string, string> = {
  'engagement.created': 'requested this engagement',
  'engagement.offer_made': 'sent an offer',
  'engagement.customer_accepted': 'accepted the offer',
  'engagement.governance_approved': 'approved the engagement',
  'engagement.governance_returned': 'returned the engagement for review',
  'engagement.revision_requested': 'requested a revision',
  'engagement.expert_declined': 'declined the request',
  'engagement.withdrawn': 'withdrew the request',
  'engagement.canceled': 'canceled the engagement',
  'engagement.closing_started': 'started closing the engagement',
  'engagement.closed': 'closed the engagement',
  'engagement.amendment_proposed': 'proposed an amendment',
  'engagement.amendment_countered': 'countered the amendment',
  'engagement.amendment_accepted': 'accepted an amendment',
  'engagement.amendment_withdrawn': 'withdrew the amendment',
  'engagement.amendment_pending_governance': 'sent the amendment to governance',
  'engagement.hours_submitted': 'submitted hours',
  'engagement.hours_confirmed': 'confirmed hours',
  'engagement.hours_partially_confirmed': 'partially confirmed hours',
  'engagement.hours_disputed': 'disputed hours',
  'engagement.hours_resolved': 'resolved a disputed submission',
  'engagement.message_sent': 'sent a message',
  'engagement.channel_joined': 'joined a message channel',
  'engagement.channel_left': 'left a message channel',
};

export function activityCopy(eventType: string): string {
  return EVENT_COPY[eventType] ?? eventType;
}
